import { APP_ROUTES } from "@/config/appConfig";
import { apiCalls } from "@/api/inspectorPortalQueries";
import { setInspectorPortalUnauthorizedHandler } from "@/config/axiosConfig";
import { AuthSession } from "@/pages/InspectorPortal/types";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";

type PendingLogin = {
  phoneNumber: string;
};

type AuthSessionContextValue = {
  session: AuthSession | null;
  pendingLogin: PendingLogin | null;
  isSessionLoading: boolean;
  startLogin: (pendingLogin: PendingLogin) => void;
  logout: () => Promise<void>;
  verifyOtp: (code: string) => Promise<boolean>;
};

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null);

export function AuthSessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [pendingLogin, setPendingLogin] = useState<PendingLogin | null>(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);

  const logout = useCallback(async () => {
    await apiCalls.logoutInspectorPortal().catch(() => null);
    setSession(null);
    setPendingLogin(null);
    navigate(APP_ROUTES.AUTH.LOGIN, {
      replace: true,
    });
  }, [navigate]);

  useEffect(() => {
    let ignore = false;

    apiCalls
      .getInspectorPortalSession()
      .then((nextSession) => {
        if (ignore) return;
        setSession({
          phoneNumber: "",
          inspectorId: nextSession.inspectorId,
          fullName: nextSession.fullName,
        });
      })
      .catch(() => {
        if (!ignore) setSession((current) => current);
      })
      .finally(() => {
        if (!ignore) setIsSessionLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    setInspectorPortalUnauthorizedHandler(() => {
      void logout();
    });

    return () => setInspectorPortalUnauthorizedHandler(null);
  }, [logout]);

  const value = useMemo<AuthSessionContextValue>(
    () => ({
      session,
      pendingLogin,
      isSessionLoading,
      startLogin: (nextPendingLogin) => {
        setPendingLogin(nextPendingLogin);
        navigate(APP_ROUTES.AUTH.OTP, {
          replace: true,
        });
      },
      logout,
      verifyOtp: async (code: string) => {
        if (!pendingLogin) {
          return false;
        }

        const result = await apiCalls.verifyInspectorPortalOtp(
          pendingLogin.phoneNumber,
          code,
        );

        flushSync(() => {
          setSession({
            phoneNumber: pendingLogin.phoneNumber,
            inspectorId: result.inspectorId,
            fullName: result.fullName,
          });
          setPendingLogin(null);
        });
        navigate(APP_ROUTES.PORTAL.VIEWINGS, {
          replace: true,
        });
        return true;
      },
    }),
    [isSessionLoading, logout, navigate, pendingLogin, session],
  );

  return (
    <AuthSessionContext.Provider value={value}>
      {children}
    </AuthSessionContext.Provider>
  );
}

export function useAuthSession() {
  const context = useContext(AuthSessionContext);

  if (!context) {
    throw new Error("useAuthSession must be used within AuthSessionProvider");
  }

  return context;
}
